import React, { useState, useEffect, useCallback, useRef } from 'react';
import './QuestionsPage.css';

interface QuestionFeedbackEntry {
  id: string;
  questionId: string;
  roomName: string;
  gameType: string;
  playerId?: string;
  playerName: string;
  rating: number | null;
  liked: boolean | null;
  correct: boolean | null;
  timestamp: number;
  questionText?: string;
  correctAnswer?: string;
}

interface QuestionsPageProps {
  socket: { emit: (event: string, data: any) => void; on: (event: string, callback: (data: any) => void) => void; off: (event: string, callback?: (data: any) => void) => void; connected: boolean } | null;
  refreshToken?: number;
}

const QuestionsPage: React.FC<QuestionsPageProps> = ({ socket, refreshToken }) => {
  const [activeTab, setActiveTab] = useState<'liked' | 'incorrect'>('liked');
  const [likedEntries, setLikedEntries] = useState<QuestionFeedbackEntry[]>([]);
  const [incorrectEntries, setIncorrectEntries] = useState<QuestionFeedbackEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const pendingPoolsRef = useRef<Set<'liked' | 'incorrect'>>(new Set());

  const fetchFeedback = useCallback((pool: 'liked' | 'incorrect') => {
    if (!socket?.connected) return;
    socket.emit('admin-get-question-feedback', { pool });
  }, [socket]);

  const fetchAllFeedback = useCallback(() => {
    if (!socket?.connected) return;
    pendingPoolsRef.current = new Set(['liked', 'incorrect']);
    setIsLoading(true);
    fetchFeedback('liked');
    fetchFeedback('incorrect');
  }, [fetchFeedback, socket]);

  useEffect(() => {
    if (!socket) return;

    const handleData = (data: { entries: QuestionFeedbackEntry[]; pool: string }) => {
      if (data.pool === 'liked') {
        setLikedEntries(data.entries || []);
        pendingPoolsRef.current.delete('liked');
      } else if (data.pool === 'incorrect') {
        setIncorrectEntries(data.entries || []);
        pendingPoolsRef.current.delete('incorrect');
      }
      if (pendingPoolsRef.current.size === 0) {
        setIsLoading(false);
      }
    };

    socket.on('admin-question-feedback-data', handleData);
    return () => {
      socket.off('admin-question-feedback-data', handleData);
    };
  }, [socket]);

  useEffect(() => {
    fetchAllFeedback();
    return undefined;
  }, [fetchAllFeedback, socket?.connected]);

  useEffect(() => {
    if (!socket?.connected) return;
    fetchAllFeedback();
  }, [fetchAllFeedback, refreshToken, socket?.connected]);

  const entries = activeTab === 'liked' ? likedEntries : incorrectEntries;

  const formatTimestamp = (ts: number) => new Date(ts).toLocaleString();

  const truncate = (text: string | undefined, len: number) => {
    if (!text) return '—';
    return text.length <= len ? text : text.slice(0, len) + '…';
  };

  const getPlayerDisplay = (entry: QuestionFeedbackEntry) => {
    return truncate(entry.playerName?.trim() || entry.playerId || '—', 24);
  };

  const handleClearAll = () => {
    if (!socket?.connected) return;
    const confirmed = window.confirm(
      'This will clear all saved question feedback (liked and incorrect) for all rooms. This cannot be undone. Continue?'
    );
    if (!confirmed) return;
    setIsClearing(true);
    socket.emit('admin-clear-question-feedback', {});
    setLikedEntries([]);
    setIncorrectEntries([]);
    setIsClearing(false);
  };

  return (
    <div className="questions-page">
      <div className="questions-header">
        <h2 className="questions-title">
          <span className="title-prefix">&gt;</span> Question Feedback
        </h2>
        <div className="questions-header-row">
          <div className="questions-tabs">
            <button
              className={`tab-button ${activeTab === 'liked' ? 'active' : ''}`}
              onClick={() => setActiveTab('liked')}
            >
              👍 Liked
              <span className="tab-count">{likedEntries.length}</span>
            </button>
            <button
              className={`tab-button ${activeTab === 'incorrect' ? 'active' : ''}`}
              onClick={() => setActiveTab('incorrect')}
            >
              ❌ Incorrect
              <span className="tab-count">{incorrectEntries.length}</span>
            </button>
          </div>
          <button
            className={`tab-button questions-clear-button ${isClearing ? 'active' : ''}`}
            type="button"
            disabled={!socket?.connected || isClearing}
            onClick={handleClearAll}
          >
            🧹 Clear All
          </button>
        </div>
      </div>

      <div className="questions-content">
        {isLoading && entries.length === 0 ? (
          <div className="questions-loading">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="questions-empty">
            <span className="empty-icon">{activeTab === 'liked' ? '👍' : '❌'}</span>
            <p>
              No {activeTab === 'liked' ? 'liked' : 'incorrect'} feedback yet
            </p>
          </div>
        ) : (
          <div className="feedback-list">
            {(entries as QuestionFeedbackEntry[]).map((entry) => (
              <div key={entry.id} className="feedback-card">
                <div className="feedback-card-header">
                  <span className="feedback-room">{entry.roomName}</span>
                  <span className="feedback-player" title="Player">{getPlayerDisplay(entry)}</span>
                  <span className="feedback-time">{formatTimestamp(entry.timestamp)}</span>
                </div>
                <div className="feedback-question">{truncate(entry.questionText, 120)}</div>
                {entry.correctAnswer && (
                  <div className="feedback-answer">Correct: {truncate(entry.correctAnswer, 60)}</div>
                )}
                <div className="feedback-meta">
                  {activeTab === 'liked' && entry.rating != null && (
                    <span className="feedback-rating" title="Stars given">{'⭐'.repeat(entry.rating)} {entry.rating} star{entry.rating !== 1 ? 's' : ''} given</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsPage;
